import React from 'react';
import { useMsal } from '@azure/msal-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { Mail, AlertTriangle, FileText, Download, Search, RefreshCw, Users, Clock, Activity, Menu, X, Loader2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import MetricCard from '../components/MetricCard';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import EmailDetail from '../components/EmailDetail';
import VersionModal from '../components/VersionModal';
import { formatDate } from '../utils/helpers';

// Auto-color Order Type tags
const TAG_COLORS = [
  'tag-blue',
  'tag-purple',
  'tag-green',
  'tag-red',
  'tag-yellow',
  'tag-teal',
  'tag-pink',
  'tag-indigo'
];

const getOrderTypeClass = (type = '') => {
  let hash = 0;
  for (let i = 0; i < type.length; i++) {
    hash = type.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % TAG_COLORS.length;
  return `tag ${TAG_COLORS[colorIndex]}`;
};

// Canonical set of 20 order types used in the tag cloud UI.
const CANONICAL_ORDER_TYPES = [
  'Order Granting Continuance',
  'Filing Deadline Orders',
  'Order Granting Motion to Change Venue',
  'Administrative Closure',
  'Telephonic/VTC Orders',
  'EOIR Notification',
  'Order Sustaining Charges',
  'Final Removal Order',
  'Written Decisions',
  'Continuance Denied',
  'Order on Motion to Reconsider',
  'Order Granting Motion to Reopen',
  'Order of Voluntary Departure Grant',
  'Termination/Dismissal Denied',
  'Order Granting Continuance, EOIR Notification',
  'Order Granting Continuance, Telephonic/VTC Orders',
  'Order Sustaining Charges, Filing Deadline Orders',
  'Order Granting Motion to Change Venue, Filing Deadline Orders',
  'Final Removal Order, Filing Deadline Orders',
  'Other'
];



const EmailView = ({ 
    // State
    data,
    selectedEmail,
    selectedDetailCard,
    viewMode,
    selectedMonth,
    selectedPriority,
    selectedCategory,
  selectedOrderType,
    searchTerm,
    currentTime,
    dateRange,
    isExporting,
    refreshCount,
    isConnectedToLive,
    connectionStatus,
    isMobileMenuOpen,
    lastUpdated,
    isLoading,
    isRefreshing,
    currentPage,
    itemsPerPage,
    sortConfig,

    // Event Handlers
    handleEmailClick,
    handleExport,
    handleManualRefresh,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    setSearchTerm,
    setSelectedPriority,
    setSelectedCategory,
  setSelectedOrderType,
    setDateRange,
    setSelectedMonth,
    setViewMode,
    setIsMobileMenuOpen,
    setSelectedDetailCard,
    setSelectedEmail,

    // Derived Data
    filteredData,
    enhancedMetrics,
    categoryCounts,
  // subjectCounts removed
    priorityCounts,
    orderTypeCounts,
    timeSeries,
    getPaginatedData,
    totalPages,
    getSortedData
 }) => { 

  const { instance } = useMsal();

    // Version Modal State
    const [isVersionModalOpen, setIsVersionModalOpen] = React.useState(false);
     // Search Suggestions State (ADD THIS)
    const [suggestions, setSuggestions] = React.useState([]);
    const [showSuggestions, setShowSuggestions] = React.useState(false);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = React.useState(-1);
    // Order Type Search State
    const [orderTypeSearch, setOrderTypeSearch] = React.useState('');

    // Generate Search Suggestions Function (ADD THIS)
    const generateSuggestions = (searchValue) => {
        if (!searchValue || searchValue.length < 2) return [];
        
        const searchLower = searchValue.toLowerCase();
        const suggestionSet = new Set();
        
        // Generate suggestions from email data
        data.forEach(email => {
            // Email ID suggestions
            if (email.email_id?.toLowerCase().includes(searchLower)) {
                suggestionSet.add(email.email_id);
            }
            
            // Subject suggestions
            if (email.subject?.toLowerCase().includes(searchLower)) {
                suggestionSet.add(email.subject);
            }
            
            // Client name suggestions
            if (email.client_name?.toLowerCase().includes(searchLower)) {
                suggestionSet.add(email.client_name);
            }
            
            // A Number suggestions
            if (email.a_number?.toLowerCase().includes(searchLower)) {
                suggestionSet.add(email.a_number);
            }
        });
        
        // Convert to array and limit to 8 suggestions
        return Array.from(suggestionSet).slice(0, 8);
    };

    // Comprehensive filtering function that applies ALL active filters
    const getFilteredData = (baseData = data) => {
        let filtered = [...baseData];

        // 1. Apply view mode and date range filters - ONLY when not in 'all' mode
        if (viewMode && viewMode !== 'all') {
            if (viewMode === 'daily' && dateRange.start) {
                // Compare using the LOCAL date string (same as the controller/table).
                // Avoid `new Date("YYYY-MM-DD")`, which JS parses as UTC and shifts
                // the day in non-UTC timezones — that was making this count 0.
                filtered = filtered.filter(d => {
                    if (!d.classification_timestamp) return false;
                    return formatDate(d.classification_timestamp, "YYYY-MM-DD") === dateRange.start;
                });
            } else if (viewMode === 'monthly' && selectedMonth) {
                filtered = filtered.filter(d => {
                    if (!d.classification_timestamp) return false;
                    const emailMonth = formatDate(d.classification_timestamp, "YYYY-MM");
                    return emailMonth === selectedMonth;
                });
            } else if (viewMode === 'yearly' && dateRange.start) {
                // dateRange.start is "YYYY-01-01"; take the year from the string
                // rather than new Date(...).getFullYear() (UTC-shift safe).
                const targetYear = String(dateRange.start).slice(0, 4);
                filtered = filtered.filter(d => {
                    if (!d.classification_timestamp) return false;
                    return formatDate(d.classification_timestamp, "YYYY") === targetYear;
                });
            }
        }

        // 2. Apply search term filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(d => 
                d.email_id?.toLowerCase().includes(searchLower) ||
                d.subject?.toLowerCase().includes(searchLower) ||
                d.client_name?.toLowerCase().includes(searchLower) ||
                d.a_number?.toLowerCase().includes(searchLower)
            );
        }

        // 3. Apply priority filter
        if (selectedPriority) {
            filtered = filtered.filter(d => d.prediction_priority_value === selectedPriority);
        }

        // 4. Apply category filter
        if (selectedCategory) {
            filtered = filtered.filter(d => d.prediction_category_value === selectedCategory);
        }

    // 5. Apply order type filter. selectedOrderType is an array (multi-select);
    // an empty array must be treated as "no filter" (note: [] is truthy in JS).
    const selectedOrderTypes = Array.isArray(selectedOrderType)
      ? selectedOrderType
      : (selectedOrderType ? [String(selectedOrderType)] : []);
    if (selectedOrderTypes.length > 0) {
      filtered = filtered.filter(d => {
        const arr = Array.isArray(d.order_type) && d.order_type.length > 0
          ? d.order_type
          : String(d.prediction_order_type_value || '').split(/[,;]\s*/).map(s => s.trim()).filter(Boolean);
        return selectedOrderTypes.some(sel => arr.includes(sel));
      });
    }

        // 6. Apply subject filter
    // subject filter removed; use order_type instead

        return filtered;
    };

    // Get high priority count with all active filters applied
    const getHighPriorityCount = () => {
        const filtered = getFilteredData();
        return filtered.filter(d => d.prediction_priority_value === "High").length;
    };

    // Get today's count - independent of view mode filters
    const getTodayCount = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Use base data, not filtered data, to always show actual today's count
        return data.filter(d => {
            if (!d.classification_timestamp) return false;
            const emailDate = new Date(d.classification_timestamp);
            emailDate.setHours(0, 0, 0, 0);
            return emailDate.getTime() === today.getTime();
        }).length;
    };

    // Get total count for monthly or yearly view - independent of other filters
    const getTotalCount = () => {
        if (viewMode === 'monthly' && selectedMonth) {
            // Monthly view: Show total for selected month
            return data.filter(d => {
                if (!d.classification_timestamp) return false;
                const emailDate = new Date(d.classification_timestamp);
                const emailMonth = formatDate(emailDate, "YYYY-MM");
                return emailMonth === selectedMonth;
            }).length;
        } else if (viewMode === 'yearly' && dateRange.start) {
            // Yearly view: Show total for selected year
            const targetYear = new Date(dateRange.start).getFullYear();
            return data.filter(d => {
                if (!d.classification_timestamp) return false;
                const emailDate = new Date(d.classification_timestamp);
                return emailDate.getFullYear() === targetYear;
            }).length;
        }
        return 0;
    };

    // Get subtitle for Total Emails card based on view mode
    const getTotalCountSubtitle = () => {
        if (viewMode === 'monthly' && selectedMonth) {
            return `Total Emails in ${selectedMonth}`;
        } else if (viewMode === 'yearly' && dateRange.start) {
            return `Total Emails in ${new Date(dateRange.start).getFullYear()}`;
        }
        return 'Total Emails';
    };

    // Get recalculated counts for charts based on filtered data
    const getRecalculatedCounts = () => {
        const filtered = getFilteredData();

    // Calculate order type counts by individual tags (array entries) and limit to top 6
    const orderTypeMap = {};
    filtered.forEach(d => {
      const arr = Array.isArray(d.order_type) && d.order_type.length > 0 ? d.order_type : (d.prediction_order_type_value ? String(d.prediction_order_type_value).split(/[,;]\s*/).map(s => s.trim()).filter(Boolean) : ['Unknown']);
      arr.forEach(ot => {
        const key = ot || 'Unknown';
        orderTypeMap[key] = (orderTypeMap[key] || 0) + 1;
      });
    });
        
        // Create array sorted by count (highest to lowest) and limit to top 6
        const recalcOrderTypeCounts = Object.entries(orderTypeMap)
            .map(([OrderType, Count]) => ({ 
                OrderType, 
                Count 
            }))
            .sort((a, b) => b.Count - a.Count)
            .slice(0, 6);

        // Recalculate priority counts
        const priorityMap = {};
        filtered.forEach(d => {
            const priority = d.prediction_priority_value || 'Unknown';
            priorityMap[priority] = (priorityMap[priority] || 0) + 1;
        });
        const recalcPriorityCounts = Object.entries(priorityMap)
            .map(([Priority, Count]) => ({ Priority, Count }))
            .sort((a, b) => {
                const order = { 'High': 3, 'Normal': 2, 'Low': 1 };
                return (order[b.Priority] || 0) - (order[a.Priority] || 0);
            });

        // Recalculate time series
        const timeMap = {};
        filtered.forEach(d => {
            if (!d.classification_timestamp) return;
            const date = new Date(d.classification_timestamp);
            let key;
            
            if (viewMode === 'yearly') {
                key = date.getFullYear().toString();
            } else if (viewMode === 'monthly') {
                key = formatDate(date, "YYYY-MM");
            } else {
                key = formatDate(date, "YYYY-MM-DD");
            }
            
            timeMap[key] = (timeMap[key] || 0) + 1;
        });
        
        const recalcTimeSeries = Object.entries(timeMap)
            .map(([Date, Count]) => ({ Date, Count }))
            .sort((a, b) => a.Date.localeCompare(b.Date));

        return {
            orderTypeCounts: recalcOrderTypeCounts,
            priorityCounts: recalcPriorityCounts,
            timeSeries: recalcTimeSeries
        };
    };

    const recalculatedCounts = getRecalculatedCounts();

    const handleHighPriorityClick = () => {
        setSelectedPriority('High');
    };

    const handleClearAllFilters = () => {
        setSearchTerm('');
  setSelectedPriority('');
  setSelectedCategory('');
    setSelectedOrderType([]);
        setDateRange({ start: '', end: '' });
        setSelectedMonth('');
        setViewMode('all');
    };

    const renderSortIndicator = (columnKey) => {
        const isActive = sortConfig.key === columnKey;
        const isAsc = sortConfig.direction === 'desc';
        
        return (
          <div className={`sort-indicators ${isActive ? 'active' : ''}`}>
            <div className={`sort-arrow sort-arrow-up ${isActive && isAsc ? 'active' : ''}`} />
            <div className={`sort-arrow sort-arrow-down ${isActive && !isAsc ? 'active' : ''}`} />
          </div>
        );
        
    };

    // filteredOrderTypes removed — not used after chart removal


    // Check if any filters are active
  const hasActiveFilters = () => {
    const orderTypeActive = Array.isArray(selectedOrderType)
      ? selectedOrderType.length > 0
      : !!selectedOrderType;
    return !!(searchTerm || selectedPriority || selectedCategory ||
          orderTypeActive || dateRange.start ||
          dateRange.end || selectedMonth || (viewMode && viewMode !== 'all'));
  };

    // Check if we should show the Total Emails card (only for monthly and yearly)
    const shouldShowTotalCard = viewMode === 'monthly' || viewMode === 'yearly';

    return (
    <div className="dashboard-container">
      {/* Enhanced Header */}
      <header className="header">
        <div className="header-content">
          <div className="header-top">
            <div className="header-left">
              <div className="header-icon-wrapper">
                {/* App logo: drop the provided image at public/logo.png */}
                <img src="/logo.png" alt="IMS Logo" style={{ height: 36, width: 'auto', objectFit: 'contain' }} />
              </div>
              <div className="header-text-wrapper">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <h1 className="header-title">
                    IMS Dashboard
                  </h1>
                  <button
                    onClick={() => setIsVersionModalOpen(true)}
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: 'var(--muted-dark)',
                      backgroundColor: 'var(--muted-light-bg)',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '9999px',
                      border: '1px solid var(--border-light)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      outline: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--border-light)';
                      e.currentTarget.style.borderColor = 'var(--border-med)';
                      e.currentTarget.style.color = 'var(--text-dark)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--muted-light-bg)';
                      e.currentTarget.style.borderColor = 'var(--border-light)';
                      e.currentTarget.style.color = 'var(--muted-dark)';
                    }}
                    title="View version updates"
                  >
                    v1.3
                  </button>
                </div>
                <p className="header-subtitle">
                  Immigration email classification dashboard
                </p>
              </div>
            </div>
            <div className="header-right">
              <div className="status-badge">
                <div className="status-dot"></div>
                <span className="status-text">
                  {`Live • ${formatDate(currentTime, "HH:mm:ss")}`}
                </span>
              </div>
              <button
                className="btn-outline"
                onClick={handleManualRefresh}
                disabled={isRefreshing}
                style={{ padding: '0.75rem', minHeight: 'auto' }}
                title="Refresh data"
              >
                {isRefreshing ? (
                  <Loader2 size={20} className="spin" />
                ) : (
                  <RefreshCw size={20} />
                )}
              </button>
              <button
                className="mobile-menu-toggle"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="mobile-menu">
            <div className="mobile-menu-content">
              <button
                className="btn btn-outline btn-full-width"
                onClick={() => {
                  handleExport();
                  setIsMobileMenuOpen(false);
                }}
                disabled={isExporting}
              >
                {isExporting ? (
                  <Loader2 size={20} className="btn-icon spin" />
                ) : (
                  <Download size={20} className="btn-icon" />
                )}
                {isExporting ? 'Exporting...' : 'Export CSV'}
              </button>
              <div className="mobile-menu-info">
                Updates: {refreshCount} | Status: {connectionStatus.toUpperCase()} | Total: {data.length.toLocaleString()}
                {lastUpdated && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                    Last updated: {formatDate(lastUpdated, "HH:mm:ss")}
                  </div>
                )}
              </div>
            <div style={{ padding: '0.5rem 1rem' }}>
              <button
                className="btn btn-outline btn-full-width"
                onClick={() => {
                  try {
                    if (instance && instance.logoutRedirect) instance.logoutRedirect();
                    else window.location.reload();
                  } catch (e) {
                    console.error('Logout failed', e);
                    window.location.reload();
                  }
                }}
              >
                Sign out
              </button>
            </div>
            </div>
          </div>
        )}
      </header>


      {/* Main Content */}
      <main className="main-content">
        {/* Search & Filter Section */}
        <div className="card">
          <div className="card-padding">
        <div className="filter-grid">
          {/* Priority */}
          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className="form-select"
          >
            <option value="">All Priorities</option>
            <option value="High">High</option>
            <option value="Normal">Normal</option>
            <option value="Low">Low</option>
          </select>

          {/* View Mode */}
          <select
            value={viewMode}
            onChange={(e) => {
              const newMode = e.target.value;
              setViewMode(newMode);
              if (newMode === 'all') {
                setDateRange({ start: '', end: '' });
                setSelectedMonth('');
              }
            }}
            className="form-select"
          >
            <option value="all">All Time</option>
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>

          {/* Subject removed - use Order Type instead */}

          {/* 🔍 Order Type (Searchable) */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search Order Type..."
              value={orderTypeSearch}
              onChange={(e) => setOrderTypeSearch(e.target.value)}
              className="form-input"
            />

          </div>
        </div>

  {/* Order Type Tag List */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            marginTop: '1rem'
          }}
        >
          {(() => {
            // Build a map of counts keyed by order type for quick lookup
            const countsMap = new Map(orderTypeCounts.map(x => [x.OrderType, x.Count]));

            // Aggregate non-canonical types into 'Other'
            let otherCount = 0;
            orderTypeCounts.forEach(x => {
              if (!CANONICAL_ORDER_TYPES.includes(x.OrderType)) otherCount += x.Count;
            });

            // Render canonical types in defined order; include 'Other' if present
            return CANONICAL_ORDER_TYPES.map(type => {
              const count = type === 'Other' ? otherCount : (countsMap.get(type) || 0);
              const selectedArr = Array.isArray(selectedOrderType) ? selectedOrderType : (selectedOrderType ? [String(selectedOrderType)] : []);
              const isActive = selectedArr.includes(type);
              const isEmpty = count === 0;
              return (
                <span
                  key={type}
                  className={`${getOrderTypeClass(type)} ${isActive ? 'tag-active' : ''}`}
                  onClick={() => {
                    const next = new Set(selectedArr);
                    if (next.has(type)) next.delete(type);
                    else next.add(type);
                    setSelectedOrderType(Array.from(next));
                  }}
                  title={`Filter by ${type}`}
                  style={{ opacity: isEmpty ? 0.45 : 1 }}
                >
                  {type}
                  <span style={{ marginLeft: '6px', opacity: 0.6 }}>({count})</span>
                </span>
              );
            });
          })()}
        </div>




            {/* Date Range Filters */}
            {viewMode !== 'all' && (
              <div className="filter-grid" style={{ marginTop: '1rem' }}>
                {viewMode === 'daily' && (
                  <input
                    type="date"
                    value={dateRange.start || ''}
                    onChange={(e) => {
                      setDateRange({ start: e.target.value, end: '' });
                    }}
                    className="form-input"
                    placeholder="Select date"
                  />
                )}
                {viewMode === 'monthly' && (
                  <input
                    type="month"
                    value={selectedMonth || ''}
                    onChange={(e) => {
                      setSelectedMonth(e.target.value);
                    }}
                    className="form-input"
                    placeholder="Select month"
                  />
                )}
                {viewMode === 'yearly' && (
                  <input
                    type="number"
                    value={dateRange.start ? new Date(dateRange.start).getFullYear() : ''}
                    onChange={(e) => {
                      const year = e.target.value;
                      if (year && year.length === 4) {
                        setDateRange({ 
                          start: `${year}-01-01`, 
                          end: `${year}-12-31` 
                        });
                      } else if (year.length === 0) {
                        setDateRange({ start: '', end: '' });
                      }
                    }}
                    className="form-input"
                    placeholder="Enter year (e.g., 2024)"
                    min="2000"
                    max="2099"
                  />
                )}
              </div>
            )}

            {hasActiveFilters() && (
              <button
                className="btn btn-outline btn-full-width"
                style={{ marginTop: '1rem' }}
                onClick={handleClearAllFilters}
              >
                <X size={16} style={{ marginRight: '0.5rem' }} />
                Clear All Filters ({filteredData.length.toLocaleString()} filtered results)
              </button>
            )}
          </div>
        </div>


        {/* Data Table with Sorting and Pagination */}
        <div className="card">
          <div className="card-header card-header-blue">
            <div className="flex-between" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <h3 className="chart-title" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: 0 }}>
                  <FileText size={22} color="var(--accent-color)" />
                  Email Data
                  <span style={{ background: 'rgba(59, 130, 246, 0.3)', color: '#dbeafe', padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.875rem', fontWeight: '600', backdropFilter: 'blur(8px)', border: '1px solid rgba(96, 165, 250, 0.2)' }}>
                    {filteredData.length.toLocaleString()}
                  </span>
                  {isRefreshing && (
                    <Loader2 size={18} className="spin" color="var(--accent-color)" />
                  )}
                </h3>
                <p style={{ color: 'var(--muted)', fontSize: '1rem', margin: '0.75rem 0 0 0' }}>
                  Click column headers to sort • Tap Email ID for details
                  {lastUpdated && ` • Last updated: ${formatDate(lastUpdated, "HH:mm:ss")}`}
                  {hasActiveFilters() && ` • ${filteredData.length} of ${data.length} emails shown`}
                </p>
              </div>
              <div className="search-wrapper" style={{ 
                maxWidth: '600px',
                minWidth: '350px',
                width: '100%',
                position: 'relative',
                zIndex: 10
              }}>
                <Search size={16} className="search-icon" />
                <input
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchTerm(value);
                    const newSuggestions = generateSuggestions(value);
                    setSuggestions(newSuggestions);
                    setShowSuggestions(newSuggestions.length > 0);
                    setSelectedSuggestionIndex(-1);
                  }}
                  onFocus={() => {
                    if (searchTerm && suggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSuggestions(false), 200);
                  }}
                  onKeyDown={(e) => {
                    if (!showSuggestions || suggestions.length === 0) return;
                    
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setSelectedSuggestionIndex(prev => 
                        prev < suggestions.length - 1 ? prev + 1 : prev
                      );
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
                    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
                      e.preventDefault();
                      setSearchTerm(suggestions[selectedSuggestionIndex]);
                      setShowSuggestions(false);
                    } else if (e.key === 'Escape') {
                      setShowSuggestions(false);
                    }
                  }}
                  placeholder="Search emails..."
                  className="form-input search-input"
                  style={{
                    width: '100%',
                    paddingRight: searchTerm ? '2.5rem' : '2rem'
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSuggestions([]);
                      setShowSuggestions(false);
                    }}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#f1f5f9'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                    title="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
                {showSuggestions && suggestions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.5rem',
                    background: 'rgba(30, 41, 59, 0.98)',
                    border: '1px solid rgba(71, 85, 105, 0.5)',
                    borderRadius: '0.75rem',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    backdropFilter: 'blur(12px)',
                    zIndex: 1000
                  }}>
                    {suggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        onClick={() => {
                          setSearchTerm(suggestion);
                          setShowSuggestions(false);
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          transition: 'background 0.15s ease',
                          borderBottom: idx < suggestions.length - 1 ? '1px solid rgba(71, 85, 105, 0.3)' : 'none',
                          background: idx === selectedSuggestionIndex ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                          color: '#e2e8f0',
                          fontSize: '0.875rem'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = idx === selectedSuggestionIndex ? 'rgba(59, 130, 246, 0.15)' : 'transparent';
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2" style={{ alignItems: 'center' }}>
                <div style={{ fontSize: '0.875rem', color: '#cbd5e1', background: 'rgba(51, 65, 85, 0.6)', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', backdropFilter: 'blur(8px)', border: '1px solid rgba(71, 85, 105, 0.3)', whiteSpace: 'nowrap' }}>
                  Page {currentPage} of {totalPages || 1}
                    {filteredData.length > 1000 && (
                    <span style={{ color: 'var(--success)', marginLeft: '0.5rem' }}>
                      ({Math.floor(filteredData.length/1000)}k+ total)
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="table-wrapper">
            <div className="table-min-width">
              <table className="data-table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell" onClick={() => handleSort('email_id')}> 
                      <div className="table-header-content">
                        <span>Email ID</span>
                        {renderSortIndicator('email_id')}
                      </div>
                    </th>
                    <th className="table-header-cell" onClick={() => handleSort('subject')}> 
                      <div className="table-header-content">
                        <span>Subject</span>
                        {renderSortIndicator('subject')}
                      </div>
                    </th>
                    <th className="table-header-cell" onClick={() => handleSort('client_name')}> 
                      <div className="table-header-content">
                        <span>Client Name</span>
                        {renderSortIndicator('client_name')}
                      </div>
                    </th>
                    <th className="table-header-cell" onClick={() => handleSort('prediction_order_type_value')}> 
                      <div className="table-header-content">
                        <span>Order Type</span>
                        {renderSortIndicator('prediction_order_type_value')}
                      </div>
                    </th>
                    <th className="table-header-cell" onClick={() => handleSort('a_number')}> 
                      <div className="table-header-content">
                        <span>A Number</span>
                        {renderSortIndicator('a_number')}
                      </div>
                    </th>
                    <th className="table-header-cell" onClick={() => handleSort('prediction_priority_value')}> 
                      <div className="table-header-content">
                        <span>Priority</span>
                        {renderSortIndicator('prediction_priority_value')}
                      </div>
                    </th>
                    <th className="table-header-cell" onClick={() => handleSort('classification_timestamp')}> 
                      <div className="table-header-content">
                        <span>Date</span>
                        {renderSortIndicator('classification_timestamp')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getPaginatedData.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8', fontSize: '1.125rem' }}>
                        {isLoading ? (
                          <LoadingSpinner />
                        ) : (
                          <div>
                            <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
                            <div>No data available</div>
                            <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                              {hasActiveFilters() 
                                ? 'No emails match your current filters. Try adjusting or clearing them.'
                                : 'Try adjusting your filters or refresh the data'
                              }
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : (
                    getPaginatedData.map((row, idx) => (
                      <tr key={row.email_id || idx} className="table-row">
                        <td className="table-cell">
                          <button
                            className="btn btn-link"
                            onClick={() => handleEmailClick(row)}
                            title={row.email_id}
                          >
                            {row.email_id.substring(0, 8)}...
                          </button>
                        </td>
                        <td className="table-cell table-cell-subject">
                          <div className="table-cell-text-truncate">
                            {row.subject}
                          </div>
                        </td>
                        <td className="table-cell table-cell-muted">{row.client_name || "Unknown"}</td>
                        <td className="table-cell">
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            {Array.isArray(row.order_type) && row.order_type.length > 0 ? (
                              row.order_type.map((ot, i) => (
                                <span
                                  key={`${row.email_id || idx}-ot-${i}`}
                                  className={getOrderTypeClass(ot)}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    const selectedArr = Array.isArray(selectedOrderType) ? selectedOrderType : (selectedOrderType ? [String(selectedOrderType)] : []);
                                    const next = new Set(selectedArr);
                                    if (next.has(ot)) next.delete(ot);
                                    else next.add(ot);
                                    setSelectedOrderType(Array.from(next));
                                  }}
                                  title={`Filter by ${ot}`}
                                >
                                  {ot}
                                </span>
                              ))
                            ) : (
                              <span
                                className={getOrderTypeClass(row.prediction_order_type_value)}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  const value = row.prediction_order_type_value;
                                  const selectedArr = Array.isArray(selectedOrderType) ? selectedOrderType : (selectedOrderType ? [String(selectedOrderType)] : []);
                                  const next = new Set(selectedArr);
                                  if (next.has(value)) next.delete(value);
                                  else next.add(value);
                                  setSelectedOrderType(Array.from(next));
                                }}
                                title="Click to filter by Order Type"
                              >
                                {(Array.isArray(row.order_type) ? row.order_type.join(', ') : row.prediction_order_type_value) || 'N/A'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="table-cell table-cell-code">{row.a_number || "N/A"}</td>
                        <td className="table-cell">
                          <span className={`priority-badge ${ 
                            row.prediction_priority_value === "High" ? 'priority-high' :
                            row.prediction_priority_value === "Normal" ? 'priority-normal' :
                            'priority-low'
                          }`}>
                            {row.prediction_priority_value === "High" && <AlertTriangle size={14} color="white" style={{ marginRight: '0.25rem' }} />}
                            {row.prediction_priority_value}
                          </span>
                        </td>
                        <td className="table-cell table-cell-muted" style={{ fontSize: '1rem', fontFamily: 'monospace' }}>
                          {row.classification_timestamp
                            ? formatDate(new Date(row.classification_timestamp), "MM-DD-YYYY HH:mm")
                            : "-"
                          }
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          {totalPages > 0 && (
            <div className="pagination-wrapper">
              <div className="pagination-info">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, getSortedData.length)} of {getSortedData.length.toLocaleString()} entries
                {hasActiveFilters() && ` (filtered from ${data.length.toLocaleString()} total)`}
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  title="First page"
                >
                  <ChevronsLeft size={16} />
                </button>
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Previous page"
                >
                  Prev
                  <ChevronLeft size={16} />
                </button>
                
                {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = idx + 1;
                  } else if (currentPage <= 3) {
                    pageNum = idx + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + idx;
                  } else {
                    pageNum = currentPage - 2 + idx;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-button ${currentPage === pageNum ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                      title={`Go to page ${pageNum}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  title="Next page"
                >
                  Next
                  <ChevronRight size={16} />
                </button>
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Last page"
                >
                  <ChevronsRight size={16} />
                </button>
                
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="pagination-select"
                  title="Items per page"
                >
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>
            </div>
          )}
        </div>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
        {/* Metric Cards - Dynamic grid based on whether Total card is shown */}
        <div
          className="metric-cards metric-cards-mobile"
          style={{
            display: 'grid',
            gridTemplateColumns: shouldShowTotalCard ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
            gap: '1.5rem',
            maxWidth: shouldShowTotalCard ? '1200px' : '800px',
            margin: '0 auto'
          }}
        >
          <MetricCard
            title="High Priority"
            value={getHighPriorityCount()}
            icon={<AlertTriangle />}
            onClick={() => {
              setSelectedPriority('High');
            }}
            cardClass="metric-card-red"
            subtitle={
              viewMode === 'daily' && dateRange.start 
                ? `High Priority on ${formatDate(new Date(dateRange.start), "MM-DD-YYYY")}` 
                : viewMode === 'monthly' && selectedMonth 
                ? `High Priority in ${selectedMonth}` 
                : viewMode === 'yearly' && dateRange.start 
                ? `High Priority in ${new Date(dateRange.start).getFullYear()}` 
                : hasActiveFilters() 
                ? 'High Priority (Filtered)' 
                : 'High Priority (All Time)'
            }
          />
          
          <MetricCard
            title="Today"
            value={getTodayCount()}
            icon={<Clock />}
            onClick={() => {
              setViewMode('daily');
              const today = new Date().toISOString().split('T')[0];
              setDateRange({ start: today, end: '' });
              setSelectedMonth('');
            }}
            cardClass="metric-card-purple"
            subtitle={
              viewMode === 'daily' && dateRange.start === new Date().toISOString().split('T')[0]
                ? `Today's Emails (${formatDate(new Date(), "MM-DD-YYYY")})`
                : hasActiveFilters() 
                ? "Today (Filtered)" 
                : "Today's Emails"
            }
          />
          
          {/* Total Emails Card - Only visible in Monthly and Yearly views */}
          {shouldShowTotalCard && (
            <MetricCard
              title="Total Emails"
              value={getTotalCount()}
              icon={<Mail />}
              onClick={() => {
                // Clicking refreshes/confirms the current view
                if (viewMode === 'monthly' && !selectedMonth) {
                  const thisMonth = formatDate(new Date(), "YYYY-MM");
                  setSelectedMonth(thisMonth);
                } else if (viewMode === 'yearly' && !dateRange.start) {
                  const thisYear = new Date().getFullYear();
                  setDateRange({ start: `${thisYear}-01-01`, end: `${thisYear}-12-31` });
                }
              }}
              cardClass="metric-card-blue"
              subtitle={getTotalCountSubtitle()}
            />
          )}
        </div>

        <br />
        <br />

        {/* Charts Section */}
        <div className="charts-section">
          {/* Emails by Order Type chart removed as per new requirements */}

          {/* Priority Pie Chart */}
                  <ChartCard
            title="Emails by Priority"
            icon={<AlertTriangle size={22} color="var(--danger)" />}
            headerClass="card-header-red"
          >
            <div className="chart-height">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={recalculatedCounts.priorityCounts}
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    innerRadius={50}
                    paddingAngle={3}
                    dataKey="Count"
                    stroke="#ffffff"
                    strokeWidth={2}
                    onClick={(data) => {
                      if (data && data.Priority) {
                        setSelectedPriority(data.Priority);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {recalculatedCounts.priorityCounts.map((entry, index) => (
                      <Cell
                        key={`priority-cell-${index}`}
                        fill={
                entry.Priority === 'High' ? 'var(--danger)' :
                  entry.Priority === 'Normal' ? 'var(--success)' :
                  'var(--neutral)'
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px -4px rgba(0, 0, 0, 0.1)',
                      fontSize: '12px',
                      padding: '8px'
                    }}
                    formatter={(value, name) => [`${value.toLocaleString()}`, 'Count']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-center gap-3" style={{ marginTop: '1rem' }}>
              {recalculatedCounts.priorityCounts.map((entry, index) => (
                <div key={`legend-${index}`} className="flex gap-1" style={{ alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedPriority(entry.Priority);
                  }}
                >
                      <div style={{ 
                    width: '0.75rem',
                    height: '0.75rem',
                    borderRadius: '50%',
                    background: entry.Priority === 'High' ? 'var(--danger)' :
                               entry.Priority === 'Normal' ? 'var(--success)' :
                               'var(--neutral)'
                  }} />
                  <span style={{ fontSize: '1.125rem', color: '#f1f5f9', fontWeight: '600', textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)' }}>
                    {entry.Priority} ({entry.Count})
                  </span>
                </div>
              ))}
            </div>
          </ChartCard>

          {/* Timeline Chart */}
          <div className="timeline-chart">
            <ChartCard
              title="Classification Timeline"
              icon={<Activity size={22} color="#a78bfa" />}
              headerClass="card-header-purple"
            >
              <div className="chart-height">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={recalculatedCounts.timeSeries.slice(-30)}
                    margin={{ top: 20, right: 30, left: 30, bottom: 50 }}
                    onClick={(data) => {
                      if (data && data.activeLabel) {
                        const clickedDate = data.activeLabel;
                        if (clickedDate.includes('-Q')) {
                          const [year, quarter] = clickedDate.split('-Q');
                          const startMonth = (parseInt(quarter) - 1) * 3 + 1;
                          const endMonth = startMonth + 2;
                          setDateRange({
                            start: `${year}-${String(startMonth).padStart(2, '0')}-01`,
                            end: `${year}-${String(endMonth).padStart(2, '0')}-31`
                          });
                          setViewMode('all');
                        } else if (clickedDate.match(/^\d{4}-\d{2}$/)) {
                          setSelectedMonth(clickedDate);
                          setViewMode('monthly');
                          setDateRange({ start: '', end: '' });
                        } else if (clickedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                          setDateRange({
                            start: clickedDate,
                            end: ''
                          });
                          setViewMode('daily');
                          setSelectedMonth('');
                        } else if (clickedDate.match(/^\d{4}$/)) {
                          setDateRange({
                            start: `${clickedDate}-01-01`,
                            end: `${clickedDate}-12-31`
                          });
                          setViewMode('yearly');
                          setSelectedMonth('');
                        }
                      }
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(241, 245, 249, 0.3)" />
                    <XAxis
                      dataKey="Date"
                      fontSize={14}
                      tick={{ fill: 'var(--e2e8f0)', fontWeight: '500', cursor: 'pointer' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(1)}k` : value.toLocaleString()}
                      fontSize={14}
                      tick={{ fill: 'var(--e2e8f0)', fontWeight: '500' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px -4px rgba(0, 0, 0, 0.1)',
                        fontSize: '12px',
                        padding: '8px'
                      }}
                      formatter={(value, name) => [`${value.toLocaleString()}`, 'Count']}
                      labelFormatter={(label) => `Click to filter by: ${label}`}
                      labelStyle={{ color: 'var(--muted-dark)', fontWeight: '500', fontSize: '12px' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="Count"
                      stroke="var(--violet)"
                      strokeWidth={3}
                      dot={{ r: 4, fill: 'var(--violet)', strokeWidth: 1, stroke: 'white', cursor: 'pointer' }}
                      activeDot={{ r: 6, fill: 'var(--violet-active)', strokeWidth: 2, stroke: 'white', cursor: 'pointer' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>
        </div>
      </main>

      {/* Metric Detail Modal */}
      {selectedDetailCard && (
        <div className="modal-overlay" onClick={() => setSelectedDetailCard(null)}>
          <div className="modal-content" style={{ maxWidth: '90vw', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-content">
                <div className="modal-header-left">
                    <div className="modal-icon-wrapper">
                    {selectedDetailCard === 'total' && <Mail size={24} color="var(--accent-start)" />}
                    {selectedDetailCard === 'high-priority' && <AlertTriangle size={24} color="var(--danger)" />}
                    {selectedDetailCard === 'active-cases' && <Users size={24} color="var(--success)" />}
                    {selectedDetailCard === 'today' && <Clock size={24} color="var(--violet)" />}
                  </div>
                  <div>
                    <h3 className="modal-title">
                      {selectedDetailCard === 'total' && 'All Emails'}
                      {selectedDetailCard === 'high-priority' && 'High Priority Emails'}
                      {selectedDetailCard === 'today' && 'Today\'s Emails'}
                    </h3>
                    <p className="modal-subtitle" style={{ fontSize: '1.125rem' }}>
                      {selectedDetailCard === 'total' && `${filteredData.length.toLocaleString()} total emails`}
                      {selectedDetailCard === 'high-priority' && `${getHighPriorityCount().toLocaleString()} high priority emails`}
                      {selectedDetailCard === 'today' && `${getTodayCount().toLocaleString()} emails processed today`}
                      {hasActiveFilters() && ' (with active filters)'}
                    </p>
                  </div>
                </div>
                <button className="modal-close" onClick={() => setSelectedDetailCard(null)}>
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh' }}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {(() => {
                  let displayData = [];
                  const filtered = getFilteredData();
                  
                  if (selectedDetailCard === 'total') {
                    displayData = filtered;
                  } else if (selectedDetailCard === 'high-priority') {
                    displayData = filtered.filter(d => d.prediction_priority_value === "High");
                  } else if (selectedDetailCard === 'today') {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    displayData = filtered.filter(d => {
                      const dt = new Date(d.classification_timestamp);
                      if (!dt) return false;
                      const emailDate = new Date(dt);
                      emailDate.setHours(0, 0, 0, 0);
                      return emailDate.getTime() === today.getTime();
                    });
                  }

                  return displayData.slice(0, 50).map((email, idx) => (
                    <div key={email.email_id || idx} style={{
                      padding: '1rem',
                      background: 'rgba(248, 250, 252, 0.05)',
                      borderRadius: '0.75rem',
                      border: '1px solid rgba(71, 85, 105, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => {
                      setSelectedDetailCard(null);
                      handleEmailClick(email);
                    }}
                    >
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                        <div>
                          <label className="form-label" style={{ fontSize: '0.875rem' }}>Email ID</label>
                          <p className="modal-text-code">{email.email_id}</p>
                        </div>
                        <div>
                          <label className="form-label" style={{ fontSize: '0.875rem' }}>Priority</label>
                          <span className={`priority-badge ${ 
                            email.prediction_priority_value === 'High' ? 'priority-high' :
                            email.prediction_priority_value === 'Normal' ? 'priority-normal' :
                            'priority-low'
                          }`}>
                            {email.prediction_priority_value}
                          </span>
                        </div>
                        <div>
                          <label className="form-label" style={{ fontSize: '0.875rem' }}>A Number</label>
                          <p className="modal-text-code">{email.a_number || "N/A"}</p>
                        </div>
                        <div>
                          <label className="form-label" style={{ fontSize: '0.875rem' }}>Date</label>
                          <p className="modal-text-code" style={{ fontSize: '1rem', color: '#e2e8f0', margin: 0 }}>
                            {email.classification_timestamp ? formatDate(email.classification_timestamp, "MM-DD HH:mm") : "-"}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="form-label" style={{ fontSize: '0.875rem' }}>Subject</label>
                        <p className="modal-text-small" style={{ fontSize: '1.125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email.subject}</p>
                      </div>
                      <div style={{ marginTop: '0.75rem' }}>
                        <label className="form-label" style={{ fontSize: '0.875rem' }}>Client Name</label>
                        <p className="modal-text-small">{email.client_name || "Unknown"}</p>
                      </div>
                    </div>
                  ));
                })()}

                {(() => {
                  let totalCount = 0;
                  const filtered = getFilteredData();
                  
                  if (selectedDetailCard === 'total') totalCount = filtered.length;
                  else if (selectedDetailCard === 'high-priority') totalCount = filtered.filter(d => d.prediction_priority_value === "High").length;
                  else if (selectedDetailCard === 'today') {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    totalCount = filtered.filter(d => {
                      const dt = new Date(d.classification_timestamp);
                      if (!dt) return false;
                      const emailDate = new Date(dt);
                      emailDate.setHours(0, 0, 0, 0);
                      return emailDate.getTime() === today.getTime();
                    }).length;
                  }

                  if (totalCount > 50) {
                    return (
                      <div className="text-center" style={{ padding: '1.5rem', color: 'var(--neutral)', fontSize: '1.125rem' }}>
                        Showing first 50 of {totalCount.toLocaleString()} emails for performance
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Email Detail Modal */}
      {selectedEmail && (
        <EmailDetail
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
        />
      )}

      {/* Version Modal */}
      <VersionModal 
        isOpen={isVersionModalOpen}
        onClose={() => setIsVersionModalOpen(false)}
      />
    </div>
  );
}

export default EmailView;